import type { FC } from 'react';

import * as React from 'react';
import { useMemo } from 'react';

import type { SectionMenu, SectionTree } from '../components/SectionMenu/SectionMenu.types';

import { buildSectionMenu } from '../components/SectionMenu/SectionMenu.utils';
import useEcommerceSections from '../hooks/useEcommerceSections';

interface ProductSectionsContextState {
    sectionTree: SectionTree;
    sectionMenu: SectionMenu | undefined;
    sectionsFetched: boolean;
}

const ProductSectionsContext = React.createContext<ProductSectionsContextState>({
    sectionTree: [],
    sectionMenu: undefined,
    sectionsFetched: false,
});

export const ProductSectionsContextProvider: FC<React.PropsWithChildren<unknown>> = ({
    children,
}) => {
    const { sectionTree, hasSections } = useEcommerceSections();

    const sectionMenu = useMemo(() => buildSectionMenu(sectionTree), [sectionTree]);

    const contextValue = useMemo(
        () => ({
            sectionMenu,
            sectionTree: sectionTree || [],
            sectionsFetched: hasSections,
        }),
        [hasSections, sectionMenu, sectionTree],
    );

    return (
        <ProductSectionsContext.Provider
            // eslint-disable-next-line react/jsx-no-constructed-context-values
            value={contextValue}
        >
            {children}
        </ProductSectionsContext.Provider>
    );
};

export const useProductSectionsContext = () => {
    const state = React.useContext<ProductSectionsContextState>(ProductSectionsContext);

    return state;
};
